import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from './AuthContext';
import { getStoreEntitlements } from 'Services/subscriptionService';

const EntitlementContext = createContext();

export default EntitlementContext;

/**
 * Holds the store's resolved plan entitlements and usage.
 *
 * Entitlements arrive on the profile payload, so gates render correctly on the
 * first paint rather than flashing locked content. `refreshEntitlements` re-reads
 * them after anything that changes the plan, such as an upgrade.
 *
 * Nothing here knows a plan by name. A gate asks whether a feature code is
 * enabled, and the answer comes from the backend, so launching a plan or moving
 * a feature between tiers needs no frontend change.
 */

export const EntitlementProvider = ({ children }) => {
  const { userProfile } = useContext(AuthContext);

  const [entitlements, setEntitlements] = useState({});
  const [usage, setUsage] = useState({});
  const [plan, setPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  // Gates stay closed until entitlements have been confirmed against the API.
  // Without this a stale cached profile can flash gated content open.
  const [isResolved, setIsResolved] = useState(false);

  const storeId = userProfile?.store?.store_id;

  /**
   * Seed from the profile payload for a fast first paint.
   *
   * This is a hint, not the truth: `userProfile` is hydrated from localStorage
   * and is only rewritten when the profile is re-fetched, so after a plan change
   * it can describe a plan the store is no longer on. The fetch below corrects
   * it, and `isResolved` keeps the gates shut until that lands.
   */
  useEffect(() => {
    const subscription = userProfile?.store?.subscription;

    if (!subscription) {
      return;
    }

    setEntitlements(subscription.entitlements || {});
    setUsage(subscription.usage || {});
    setPlan(subscription.plan || null);
    setSubscriptionStatus(subscription.status || null);
  }, [userProfile]);

  /**
   * Re-read entitlements from the API. Used after an upgrade, where the profile
   * payload in memory is stale but the gates must update immediately.
   *
   * @param {Number|String} storeId optional store identifier, required for hosts
   * @returns {void}
   */
  const refreshEntitlements = useCallback(async (storeId) => {
    try {
      setLoading(true);
      const res = await getStoreEntitlements(storeId);
      const data = res?.data?.data;

      if (data) {
        setEntitlements(data.entitlements || {});
        setUsage(data.usage || {});
        setPlan(data.plan || null);
        setSubscriptionStatus(data.status || null);
      }
    } catch (error) {
      // A failed refresh leaves the previously resolved entitlements in place
      // rather than blanking the UI into a fully locked state.
      console.error('Failed to refresh entitlements', error);
    } finally {
      setLoading(false);
      setIsResolved(true);
    }
  }, []);

  /**
   * Confirm entitlements against the API whenever the store changes.
   *
   * The API is the authority here. Seeding alone was not enough: a store that
   * changes plan keeps the old entitlements in its cached profile until
   * something re-fetches it, which left gated features open on the previous
   * plan's terms.
   */
  useEffect(() => {
    if (!storeId) {
      // Hosts and guests have no store of their own. Their gates are resolved
      // per demo by the backend, so there is nothing to wait for here.
      setIsResolved(true);
      return;
    }

    refreshEntitlements(storeId);
  }, [storeId, refreshEntitlements]);

  /**
   * Whether the store's plan includes a feature.
   *
   * @param {String} featureCode feature code, e.g. 'ai_summary'
   * @returns {Boolean} true when the plan grants it
   */
  const hasFeature = useCallback(
    (featureCode) => Boolean(entitlements?.[featureCode]?.enabled),
    [entitlements]
  );

  /**
   * Numeric allowance for a feature.
   *
   * @param {String} featureCode feature code, e.g. 'hosts_limit'
   * @returns {Number|null} the allowance, or null when unlimited
   */
  const getLimit = useCallback(
    (featureCode) => {
      const entitlement = entitlements?.[featureCode];
      if (!entitlement?.enabled) {
        return 0;
      }
      return entitlement.limit ?? null;
    },
    [entitlements]
  );

  const contextData = useMemo(
    () => ({
      entitlements,
      usage,
      plan,
      subscriptionStatus,
      loading,
      isResolved,
      hasFeature,
      getLimit,
      refreshEntitlements,
    }),
    [
      entitlements,
      usage,
      plan,
      subscriptionStatus,
      loading,
      isResolved,
      hasFeature,
      getLimit,
      refreshEntitlements,
    ]
  );

  return (
    <EntitlementContext.Provider value={contextData}>
      {children}
    </EntitlementContext.Provider>
  );
};
